import { useState, useEffect } from 'react';
import { Loader2, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';

function Field({ label, children, mismatch }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        {label}
      </span>
      <span className={mismatch ? 'text-sm text-red-600 font-medium' : 'text-sm'}>
        {children}
      </span>
    </div>
  );
}

function ModuleHash({ entry }) {
  if (!entry.loaded) {
    return (
      <span className="text-red-600">not loaded</span>
    );
  }
  return (
    <span className="font-mono text-xs">
      {entry.source_hash || 'n/a'}
      {entry.source_file && (
        <span className="text-muted-foreground ml-2">({entry.source_file})</span>
      )}
    </span>
  );
}

function CacheHeaderRow({ label, result }) {
  if (!result) {
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="w-40 shrink-0 font-medium">{label}</span>
        <span className="text-muted-foreground">not checked</span>
      </div>
    );
  }
  if (result.error) {
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="w-40 shrink-0 font-medium">{label}</span>
        <span className="text-red-500">{result.error}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-40 shrink-0 font-medium">{label}</span>
      <code className="font-mono bg-muted px-1.5 py-0.5 rounded">
        {result.cacheControl || '(no cache-control header)'}
      </code>
      <span className="text-muted-foreground">{result.url}</span>
    </div>
  );
}

/** Fetches *url* with the browser's DEFAULT cache behavior (no cache-
 * busting) -- the point is to observe whatever the browser/CDN would
 * actually serve a normal page load, which is exactly what a stale-cache
 * bug looks like. A cache-busting fetch would defeat the check it exists
 * to run. */
async function _checkCacheHeader(url) {
  try {
    const res = await fetch(url, { method: 'GET' });
    return { url, cacheControl: res.headers.get('cache-control'), error: null };
  } catch (err) {
    return { url, cacheControl: null, error: err.message || 'fetch failed' };
  }
}

async function _runCacheAudit() {
  const swRegs = ('serviceWorker' in navigator)
    ? await navigator.serviceWorker.getRegistrations().catch(() => [])
    : [];
  const serviceWorkers = swRegs.map((r) => ({
    scope: r.scope,
    scriptURL: r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || null,
  }));

  // First same-origin hashed script tag actually on the page, so this
  // reflects the REAL emitted bundle, not a guessed path.
  const scriptTags = Array.from(document.querySelectorAll('script[src]'))
    .map((s) => s.getAttribute('src'))
    .filter((src) => src && src.startsWith('/static/'));
  const hashedBundlePath = scriptTags[0] || null;
  // CRA's default content-hash is 8 hex chars between dots, e.g. main.a1b2c3d4.js
  const looksContentHashed = hashedBundlePath ? /\.[0-9a-f]{8}\.[a-z]+$/i.test(hashedBundlePath) : null;

  const [indexHeader, bundleHeader] = await Promise.all([
    _checkCacheHeader('/'),
    hashedBundlePath ? _checkCacheHeader(hashedBundlePath) : Promise.resolve(null),
  ]);

  return { serviceWorkers, hashedBundlePath, looksContentHashed, indexHeader, bundleHeader };
}

export function RuntimeFingerprintCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [frontendBuild, setFrontendBuild] = useState(null);
  const [frontendBuildError, setFrontendBuildError] = useState(null);
  const [cacheAudit, setCacheAudit] = useState(null);

  useEffect(() => {
    let cancelled = false;
    diagnosticsAPI.runtimeFingerprint()
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to fetch runtime fingerprint'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    // Default (non-cache-busted) fetch -- see _checkCacheHeader.
    fetch('/build-info.json')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((d) => { if (!cancelled) setFrontendBuild(d); })
      .catch((err) => { if (!cancelled) setFrontendBuildError(err.message || 'build-info.json unavailable'); });

    _runCacheAudit().then((r) => { if (!cancelled) setCacheAudit(r); });

    return () => { cancelled = true; };
  }, []);

  const matchesHead = data?.verdict?.matches_head;
  const backendHash = data?.git_head?.hash;
  const frontendHash = frontendBuild?.commit;
  const buildsMismatch = Boolean(backendHash && frontendHash && backendHash !== frontendHash);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          Runtime Code Fingerprint
          {data && (
            matchesHead
              ? <Badge variant="default" className="ml-2">code matches HEAD</Badge>
              : <Badge variant="destructive" className="ml-2">MISMATCH</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}
        {data && (
          <div className="space-y-4">
            {/* Verdict */}
            <div className="flex items-start gap-2">
              {matchesHead ? (
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              )}
              <div>
                {matchesHead ? (
                  <p className="text-sm text-green-700">
                    Code matches HEAD expectations.
                  </p>
                ) : (
                  <div className="space-y-1">
                    {data.verdict.mismatches.map((m, i) => (
                      <p key={i} className="text-sm text-red-600">{m}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Build info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="BUILD_COMMIT">
                <code className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded">
                  {data.build_commit}
                </code>
                <Badge variant="secondary" className="ml-2 text-xs">
                  {data.build_commit_source}
                </Badge>
              </Field>
              <Field label="git rev-parse HEAD (live)">
                <code className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded">
                  {data.git_head?.hash || 'unavailable'}
                </code>
                {data.git_head?.error && (
                  <span className="text-xs text-red-500 ml-2">{data.git_head.error}</span>
                )}
              </Field>
              <Field label="Working directory">
                <code className="font-mono text-xs">{data.git_head?.cwd || 'unknown'}</code>
              </Field>
              <Field label="Process start">
                {data.process_start
                  ? new Date(data.process_start).toLocaleString()
                  : 'unavailable'}
              </Field>
              <Field label="Python version">
                <code className="font-mono text-xs">{data.python_version}</code>
              </Field>
              <Field label="Frontend bundle commit" mismatch={buildsMismatch}>
                {frontendBuild ? (
                  <>
                    <code className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded">
                      {frontendBuild.commit}
                    </code>
                    {buildsMismatch && (
                      <Badge variant="destructive" className="ml-2 text-xs">
                        differs from backend HEAD
                      </Badge>
                    )}
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {frontendBuildError || 'loading...'}
                  </span>
                )}
              </Field>
              <Field label="Frontend build time">
                {frontendBuild?.build_time
                  ? new Date(frontendBuild.build_time).toLocaleString()
                  : 'unavailable'}
              </Field>
            </div>

            {buildsMismatch && (
              <div className="flex items-start gap-2 border border-red-300 dark:border-red-800 rounded-md p-2 bg-red-50 dark:bg-red-950/30">
                <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                <p className="text-xs text-red-700 dark:text-red-400">
                  Backend git HEAD ({backendHash}) and the frontend bundle's baked-in commit
                  ({frontendHash}) disagree -- one of the two redeployed and the other didn't, or
                  the browser is serving a stale frontend bundle. See the cache audit below and
                  compare a normal tab against a private window.
                </p>
              </div>
            )}

            {/* Recent commits (last 10 on live HEAD) */}
            <div className="border-t pt-3">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Recent commits (backend git log, live)
              </h4>
              {data.recent_commits?.error ? (
                <p className="text-xs text-red-500">{data.recent_commits.error}</p>
              ) : (
                <div className="space-y-0.5">
                  {data.recent_commits?.commits?.map((c) => (
                    <div key={c.hash} className="flex items-center gap-2 text-xs">
                      <code className="font-mono bg-muted px-1 py-0.5 rounded shrink-0">{c.hash}</code>
                      <span className="text-muted-foreground shrink-0 w-40">
                        {new Date(c.date).toLocaleString()}
                      </span>
                      <span className="truncate">{c.subject}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cache audit */}
            <div className="border-t pt-3">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Cache audit (this browser, this page load)
              </h4>
              {!cacheAudit ? (
                <p className="text-xs text-muted-foreground">Checking...</p>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-40 shrink-0 font-medium">Service workers</span>
                    {cacheAudit.serviceWorkers.length === 0 ? (
                      <span className="text-green-700 dark:text-green-500">none registered</span>
                    ) : (
                      <span className="text-red-600">
                        {cacheAudit.serviceWorkers.length} registered (unregistered on this app
                        load -- see src/index.js) -- reload to confirm gone:{' '}
                        {cacheAudit.serviceWorkers.map((r) => r.scriptURL || r.scope).join(', ')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-40 shrink-0 font-medium">Bundle filename hashed</span>
                    {cacheAudit.hashedBundlePath ? (
                      <>
                        <span className={cacheAudit.looksContentHashed ? 'text-green-700 dark:text-green-500' : 'text-red-600'}>
                          {cacheAudit.looksContentHashed ? 'yes' : 'NO -- filename has no content hash'}
                        </span>
                        <code className="font-mono text-muted-foreground">{cacheAudit.hashedBundlePath}</code>
                      </>
                    ) : (
                      <span className="text-muted-foreground">no /static/ script tag found on this page</span>
                    )}
                  </div>
                  <CacheHeaderRow label="index (/) cache-control" result={cacheAudit.indexHeader} />
                  <CacheHeaderRow label="bundle cache-control" result={cacheAudit.bundleHeader} />
                  <p className="text-xs text-muted-foreground pt-1">
                    Expected shape: index.html cache-control should force revalidation
                    (no-cache/max-age=0) since it references the current hashed bundle by name;
                    the hashed bundle itself is safe to cache long/immutable because any content
                    change ships under a new filename. These headers are set by whatever serves
                    the built frontend outside this repo -- this only reports what the browser
                    actually received, it cannot change hosting-layer config.
                  </p>
                </div>
              )}
            </div>

            {/* Transaction model introspection */}
            <div className="border-t pt-3">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Transaction Model (route-bound)
              </h4>
              {data.transaction_model?.resolved ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Model class">
                    {data.transaction_model.model_name}
                  </Field>
                  <Field
                    label="description annotation"
                    mismatch={
                      data.transaction_model.description_annotation &&
                      !data.transaction_model.description_annotation.includes('None') &&
                      !data.transaction_model.description_annotation.includes('Optional')
                    }
                  >
                    <code className="font-mono text-xs">
                      {data.transaction_model.description_annotation}
                    </code>
                  </Field>
                  <Field label="description default">
                    <code className="font-mono text-xs">
                      {data.transaction_model.description_default}
                    </code>
                  </Field>
                </div>
              ) : (
                <p className="text-sm text-red-500">
                  {data.transaction_model?.error || 'Could not resolve model'}
                </p>
              )}
            </div>

            {/* Dashboard introspection */}
            <div className="border-t pt-3">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Dashboard Module
              </h4>
              <Field
                label="_apply_scope_filter"
                mismatch={data.dashboard?.module_loaded && !data.dashboard?.has_apply_scope_filter}
              >
                {!data.dashboard?.module_loaded
                  ? 'module not loaded'
                  : data.dashboard.has_apply_scope_filter
                    ? 'present'
                    : 'MISSING'}
              </Field>
            </div>

            {/* Module hashes */}
            <div className="border-t pt-3">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Module Source Hashes
              </h4>
              <div className="space-y-1">
                {data.module_hashes?.map((entry) => (
                  <div key={entry.module} className="flex items-center gap-2">
                    <span className="text-xs font-medium w-40 shrink-0">{entry.module}</span>
                    <ModuleHash entry={entry} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
